import moment from 'moment';
import React, { FC, ReactElement, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import * as A from 'fp-ts/Array';
import { pipe } from 'fp-ts/function';
import * as O from 'fp-ts/Option';

import { useDebouncedCallback } from '@/shared/modules/mantine/v8/use-debounced-callback';

import { Api, Site } from '../../common/model';
import { changeArrival, changeDeparture } from '../actions/booking';
import { persistCart } from '../actions/cart';
import { fetchSites } from '../actions/site';
import { clearTripInfos, fetchTripInfos, setTripStatus } from '../actions/trip';
import { useAutoSetCheckpoints } from '../hooks';
import { TripStatus } from '../model';
import type { State } from '../reducers';
import EndStationTrip from './EndStationTrip';
import { FlightSection } from './flights/FlightSection';
import type { FormContent } from './Form';
import MeetingPoint from './MeetingPoint';

interface Props {
  forceValidation: boolean;
}

const TravelInfo: FC<Props> = ({ forceValidation }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { departure, arrival, arrivalSlots, isFetchingArrivalSlots } = useSelector((state: State) => state.booking);
  const trip = useSelector((state: State) => state.trip);
  const { sites } = useSelector((state: State) => state.sites);

  useEffect(() => {
    dispatch(fetchSites());
  }, [dispatch]);

  const onChanged = useCallback(
    (onChangedFn: (content: FormContent) => void) => (info: FormContent) => {
      onChangedFn(info);
      dispatch(persistCart());
    },
    [dispatch],
  );

  const onDepartureChanged = useCallback(
    (content: FormContent) => dispatch(changeDeparture(content, false)),
    [dispatch],
  );
  const onArrivalChanged = useCallback((content: FormContent) => dispatch(changeArrival(content)), [dispatch]);

  const departureSite = useMemo(
    () =>
      pipe(
        O.fromNullable(departure.site),
        O.flatMap(id => A.findFirst((site: Api.Site) => site.id.toString() === id)(sites)),
      ),
    [departure.site, sites],
  );

  const arrivalSite = pipe(
    O.fromNullable(arrival.site),
    O.flatMap(id => A.findFirst((site: Api.Site) => site.id.toString() === id)(sites)),
  );

  useAutoSetCheckpoints({
    departureSite,
    departureCheckpoint: departure.checkpoint,
    arrivalCheckpoint: arrival.checkpoint,
  });

  const kind = pipe(
    arrivalSite,
    O.match(
      () => Site.Kind.Airport,
      ({ type }) => type,
    ),
  );

  const debounceFetchEndTrip = useDebouncedCallback(
    (site: number, date: string, tripNumber: string, signal: AbortSignal | undefined) =>
      dispatch(fetchTripInfos('end', site, date, tripNumber, signal)),
    1000,
  );

  const fetchEndTrip = (date: string) => (site: number, tripNumber: string, signal: AbortSignal | undefined) => {
    debounceFetchEndTrip(site, date, tripNumber, signal);
  };

  const setEndTripStatus = (status: TripStatus) => dispatch(setTripStatus('end', status));
  const setEndTripUnknown = (value: boolean) =>
    dispatch(setTripStatus('end', value ? TripStatus.Unknown : TripStatus.None));
  const selectEndTrip = () => dispatch(setTripStatus('end', TripStatus.Found));
  const forceEndTrip = () => dispatch(setTripStatus('end', TripStatus.Forced));
  const clearEndTrip = () => dispatch(clearTripInfos('end'));

  const getFlightSection = (arrivalSite: Api.Site) => {
    if (!arrival.date || !arrival.hour) return null;
    return (
      <FlightSection
        arrivalSite={arrivalSite}
        content={{ ...arrival, date: arrival.date, hour: arrival.hour }}
        onChange={onChanged(onArrivalChanged)}
        trip={trip.end}
        setEndTripStatus={setEndTripStatus}
        slots={arrivalSlots}
        isFetchingSlots={isFetchingArrivalSlots}
      />
    );
  };

  const getEndStationSection = (arrivalSite: Api.Site) => (
    <EndStationTrip
      arrivalSite={arrivalSite}
      content={arrival}
      onChange={onChanged(onArrivalChanged)}
      selectEndTrip={selectEndTrip}
      forceEndTrip={forceEndTrip}
      setEndTripUnknown={setEndTripUnknown}
      clearEndTrip={clearEndTrip}
      fetchEndTrip={fetchEndTrip(moment(arrival.date).format('DD/MM/YYYY'))}
      trip={trip.end}
      slots={arrivalSlots}
      forceValidation={forceValidation}
    />
  );

  const travelInput: Record<Site.Kind, ReactElement | null> = {
    [Site.Kind.Airport]: pipe(arrivalSite, O.map(getFlightSection), O.toNullable),
    [Site.Kind.Station]: pipe(arrivalSite, O.map(getEndStationSection), O.toNullable),
  };

  return (
    <>
      <MeetingPoint
        label={t('checkpoint-departure')}
        sites={sites}
        content={departure}
        onChanged={onChanged(onDepartureChanged)}
        forceValidation={forceValidation}
      />
      <MeetingPoint
        label={t('checkpoint-arrival')}
        sites={sites}
        content={arrival}
        onChanged={onChanged(onArrivalChanged)}
        forceValidation={forceValidation}
      />

      <div className="MeetingPointNotice">
        <svg>
          <use xlinkHref="#icon__info" />
        </svg>
        <div>{t('booking-meeting-point-notice')}</div>
      </div>

      <div className="Separator" />

      {travelInput[kind]}
    </>
  );
};

export default TravelInfo;
